import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ChevronDown, TrendingUp, BarChart3 } from 'lucide-react';

const Top10Dashboard = ({ 
  title, 
  data, 
  dataKey, 
  valueKey, 
  color = 'var(--accent)',
  height = 400 
}) => {
  const [cidadeSelecionada, setCidadeSelecionada] = useState('Todas');
  const [mostrarDropdown, setMostrarDropdown] = useState(false);

  // Extrair lista única de cidades
  const cidades = useMemo(() => {
    if (!data || data.length === 0) return ['Todas'];
    const cidadesUnicas = [...new Set(data.map(item => item.cidade).filter(Boolean))];
    return ['Todas', ...cidadesUnicas.sort()];
  }, [data]);

  // Filtrar dados baseado na cidade selecionada
  const dadosFiltrados = useMemo(() => {
    if (!data || data.length === 0) return [];
    if (cidadeSelecionada === 'Todas') {
      return data;
    }
    return data.filter(item => item.cidade === cidadeSelecionada);
  }, [data, cidadeSelecionada]);

  // Preparar dados para o gr?fico (agregado por produto)
  const dadosGrafico = useMemo(() => {
    if (!dadosFiltrados || dadosFiltrados.length === 0) return [];

    const agregados = new Map();
    dadosFiltrados.forEach((item) => {
      const chave = item[dataKey] || 'N/A';
      const atual = agregados.get(chave) || {
        [dataKey]: chave,
        cidade: cidadeSelecionada === 'Todas' ? 'Todas' : cidadeSelecionada,
        subgrupo: item.subgrupo || item.subgrupo_produto || null,
        [valueKey]: 0
      };
      atual[valueKey] = (Number(atual[valueKey]) || 0) + (Number(item[valueKey]) || 0);
      agregados.set(chave, atual);
    });

    return Array.from(agregados.values())
      .sort((a, b) => (b[valueKey] || 0) - (a[valueKey] || 0))
      .slice(0, 10)
      .map((item, index) => ({
        ...item,
        posicao: index + 1,
        nomeCurto: item[dataKey]?.length > 20 
          ? `${item[dataKey].substring(0, 20)}...` 
          : item[dataKey]
      }));
  }, [dadosFiltrados, dataKey, valueKey, cidadeSelecionada]);

  const formatarValor = (valor) => {
    if (typeof valor === 'number') {
      return valor.toLocaleString('pt-BR');
    }
    return valor;
  };

  const CustomTooltip = ({ active, payload, label: _label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-[color:var(--surface)] p-3 border border-[color:var(--border)] rounded-lg shadow-sm">
          <p className="font-semibold text-[color:var(--text-primary)]">{data[dataKey]}</p>
          <p className="text-sm text-[color:var(--text-muted)]">
            <span className="font-medium">Posição:</span> {data.posicao}°
          </p>
          <p className="text-sm text-[color:var(--text-muted)]">
            <span className="font-medium">Cidade:</span> {data.cidade || cidadeSelecionada}
          </p>
          <p className="text-sm text-[color:var(--text-muted)]">
            <span className="font-medium">Subgrupo:</span> {data.subgrupo}
          </p>
          <p className="text-sm font-semibold text-[color:var(--accent)]">
            <span className="font-medium">Valor:</span> {formatarValor(data[valueKey])}
          </p>
        </div>
      );
    }
    return null;
  };

  const temDados = dadosGrafico && dadosGrafico.length > 0;
  const maiorValor = temDados 
    ? Math.max(...dadosGrafico.map(item => item[valueKey] || 0))
    : 0;

  return (
    <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)] p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-[color:var(--text-primary)] flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-[color:var(--accent)]" />
          {title}
        </h3>
        
        {/* Dropdown de Cidades */}
        <div className="relative">
          <button
            onClick={() => setMostrarDropdown(!mostrarDropdown)}
            className="inline-flex items-center px-4 py-2 border border-[color:var(--border)] rounded-md shadow-sm bg-[color:var(--surface-muted)] text-sm font-medium text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[color:var(--surface)] focus:ring-[color:var(--accent)]"
          >
            {cidadeSelecionada}
            <ChevronDown className="ml-2 h-4 w-4" />
          </button>
          
          {mostrarDropdown && (
            <div className="absolute right-0 mt-2 w-56 rounded-md shadow-sm bg-[color:var(--surface)] border border-[color:var(--border)] z-10">
              <div className="py-1 max-h-60 overflow-auto">
                {cidades.map((cidade) => (
                  <button
                    key={cidade}
                    onClick={() => {
                      setCidadeSelecionada(cidade);
                      setMostrarDropdown(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-[color:var(--surface-muted)] ${
                      cidadeSelecionada === cidade 
                        ? 'bg-[color:var(--surface-muted)] text-[color:var(--accent)] font-medium' 
                        : 'text-[color:var(--text-muted)]'
                    }`}
                  >
                    {cidade}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Gráfico ou Mensagem de Estado Vazio */}
      <div style={{ height: `${height}px` }}>
        {temDados ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={dadosGrafico}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 60,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis 
                dataKey="nomeCurto"
                angle={-45}
                textAnchor="end"
                height={80}
                fontSize={12}
                tick={{ fill: 'var(--text-muted)' }}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={{ stroke: 'var(--border)' }}
              />
              <YAxis 
                tickFormatter={formatarValor}
                fontSize={12}
                tick={{ fill: 'var(--text-muted)' }}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={{ stroke: 'var(--border)' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey={valueKey} 
                fill={color}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-[color:var(--text-muted)]">
            <div className="bg-[color:var(--surface-muted)] rounded-full p-4 mb-4">
              <BarChart3 className="h-10 w-10 text-[color:var(--text-muted)]" strokeWidth={1.5} />
            </div>
            <p className="font-medium text-[color:var(--text-muted)] mb-1">
              Nenhum dado disponível
            </p>
            <p className="text-sm text-[color:var(--text-muted)] text-center max-w-xs">
              {cidadeSelecionada === 'Todas' 
                ? 'Não há dados para os filtros selecionados'
                : `Não há dados disponíveis para a cidade "${cidadeSelecionada}"`
              }
            </p>
          </div>
        )}
      </div>

      {/* Estatísticas */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[color:var(--surface-muted)] rounded-lg p-3">
          <p className="text-sm font-medium text-[color:var(--text-muted)]">Total de Itens</p>
          <p className="text-lg font-semibold text-[color:var(--text-primary)]">
            {dadosGrafico.length}
          </p>
        </div>
        <div className="bg-[color:var(--surface-muted)] rounded-lg p-3">
          <p className="text-sm font-medium text-[color:var(--text-muted)]">Cidade Selecionada</p>
          <p className="text-lg font-semibold text-[color:var(--text-primary)]">
            {cidadeSelecionada}
          </p>
        </div>
        <div className="bg-[color:var(--surface-muted)] rounded-lg p-3">
          <p className="text-sm font-medium text-[color:var(--text-muted)]">Maior Valor</p>
          <p className={`text-lg font-semibold ${temDados ? 'text-[color:var(--status-good)]' : 'text-[color:var(--text-muted)]'}`}>
            {temDados ? formatarValor(maiorValor) : '0'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Top10Dashboard;


